import { LoaderFunction } from 'react-router-dom';
import { AuthContextType } from '../../../../hooks/useAuth';

// GQL
import { client } from '../../../../gql/client';
import {
  FETCH_PREDEFINED_ORDERS,
  FETCH_SUB_CAPITOLS,
} from '../../../../gql/queries/orders/predefinedData';
import { GET_SELECTORS_DATA } from '../../../../gql/queries/orders/orders';
import {
  FETCH_CAPITOLS,
  FETCH_CLASSIFICATIONS,
} from '../../../../gql/queries/orders/predefinedData';
import { selectOrderByIdForFillFormData } from '../../../../gql/selectors/orders';

// Sentry
import * as Sentry from '@sentry/react';

// Data static
import { groupLanes, groupTracks } from '../../../../data/orders';

// Compression
import { claimDraftScope } from '../../../../utils/draftOwnership';
import { CAMPAIGN_EDIT_SCOPE } from '../../../../utils/draftScopes';
import { readOwnDraftCopy } from '../../../../utils/ownedDraftCopy';

// naming
import { formOrderNaming } from '../../../../components/forms/NewWorkOrderForm/constants';
import { GET_USER_BY_ID } from '../../../../gql/queries/users';

const loadCampaignSummary = async (idOrder: string) => {
  try {
    const workOrderData = await selectOrderByIdForFillFormData(idOrder, true);

    const operatorData = await client.query({
      query: GET_USER_BY_ID,
      variables: {
        input: {
          id: workOrderData?.userId,
        },
      },
    });

    return {
      ...workOrderData,
      operator: `${operatorData.data.user.name} ${
        operatorData.data.user.surName.split(' ')[0]
      }`,
    };
  } catch (err) {
    Sentry.captureException(err);
    console.log('> Work Order Summary loading error:', err);
    return true;
  }
};

const loadFormOptions = async (userData: AuthContextType | null) => {
  try {
    if (userData?.user) {
      const [
        { data },
        {
          data: { capitols },
        },
        {
          data: { classifications },
        },
        {
          data: { predefinedOrders },
        },
        {
          data: { subCapitols },
        },
      ] = await Promise.all([
        client.query({
          query: GET_SELECTORS_DATA,
        }),
        client.query({
          query: FETCH_CAPITOLS,
        }),
        client.query({
          query: FETCH_CLASSIFICATIONS,
        }),
        client.query({
          query: FETCH_PREDEFINED_ORDERS,
        }),
        client.query({
          query: FETCH_SUB_CAPITOLS,
        }),
      ]);

      return {
        ...data,
        capitols,
        classifications,
        groupTracks,
        groupLanes,
        predefinedOrders,
        subCapitols,
      };
    }
  } catch (err) {
    Sentry.captureException(err);
    console.log('> Work Order Summary loading error:', err);
    return true;
  }
};

export const EditCampaignLoader: (
  userData: AuthContextType | null
) => LoaderFunction | undefined =
  (userData) =>
  async ({ params }) => {
  // Se reclama SÍNCRONO, antes de cualquier `await`: la copia de otra campaña
  // (atrás, URL a mano, deep link) se descarta aquí y nunca se monta sobre la
  // de esta URL.
  claimDraftScope(CAMPAIGN_EDIT_SCOPE, params.id);

  // El trabajo entero vive aquí dentro, íntegro. Solo ha dejado de
  // esperarse: la promesa tiene que llegar PENDIENTE al `<Await>` para que
  // el router termine la navegación al instante y el `<Fallback>` enseñe el
  // Loader DENTRO de la pantalla nueva.
  const load = async () => {
    let dataFromWorkOrderToDuplicate = {};

    if (params.id) {
      dataFromWorkOrderToDuplicate = await loadCampaignSummary(params.id);
    }

    // La copia solo si es de ESTA campaña; sin `:id` no hay de quién sea y
    // manda el servidor.
    const sessionStorageData = readOwnDraftCopy(
      formOrderNaming.EDIT_CAMPAIGN_FORM,
      params.id
    );

    const loaderToResolve = await loadFormOptions(userData);

    if (sessionStorageData) {
      const combinedData = {
        ...loaderToResolve,
        ...dataFromWorkOrderToDuplicate,
        ...sessionStorageData,
      };

      return combinedData;
    }

    const combinedData = {
      ...loaderToResolve,
      ...dataFromWorkOrderToDuplicate,
    };
    return combinedData;
  };

  return { data: load() };
};
